import { useMemo, useState } from 'react';
import type { EventCategory, RequestRecord, Status, StatusEvent, WorkflowState } from '@air/shared';
import {
  STATUS_LABELS,
  STATUS_BADGE_KEY,
  actorKindFor,
  deriveWorkflowState,
  eventCategory,
  eventVerb,
} from '@air/shared';
import { formatDate, formatDateTime, formatRelative, initialsFor } from '../lib/format';

export function StatusBadge({ status }: Readonly<{ status: Status }>) {
  return <span className={`badge badge--${STATUS_BADGE_KEY[status]}`}>{STATUS_LABELS[status]}</span>;
}

export function Icon({ name, size = 14, tint = false }: Readonly<{ name: string; size?: number; tint?: boolean }>) {
  return (
    <img
      src={`/design-system/assets/icons/${name}.svg`}
      width={size}
      height={size}
      alt=""
      className={`icon ${tint ? 'icon-tint' : ''}`}
    />
  );
}

// Step state for the 4-circle StageTracker.
//
// `state` is the derived WorkflowState — single source of truth for "where
// is this request and who owns it now". The Decision (stage 4) circle is
// neutral until a final decision lands, so clarification states no longer
// leak into it.
type StepState = 'done' | 'current' | 'blocked' | 'rejected' | 'conditional' | 'pending';

function stepStateFor(idx: number, state: WorkflowState, status: Status, events: StatusEvent[]): StepState {
  const oneBasedStage = idx + 1; // step indices are 0..3, stages are 1..4

  if (status === 'rejected') {
    const rejectedAtIt = events.some((e) => e.eventType === 'it_rejected');
    const rejectStage = rejectedAtIt ? 2 : 3;
    if (oneBasedStage === rejectStage) return 'rejected';
    if (oneBasedStage < rejectStage) return 'done';
    if (oneBasedStage === 4) return 'rejected';
    return 'pending';
  }
  if (status === 'approved_with_conditions' && oneBasedStage === 4) return 'conditional';
  if (status === 'approved' && oneBasedStage === 4) return 'done';

  if (oneBasedStage < state.stage) return 'done';
  if (oneBasedStage === state.stage) return state.blocked ? 'blocked' : 'current';
  return 'pending';
}

function nodeGlyph(s: StepState, idx: number): string {
  switch (s) {
    case 'done':
      return '✓';
    case 'rejected':
      return '×';
    case 'conditional':
      return '!';
    case 'blocked':
      return '⏸';
    default:
      return String(idx + 1);
  }
}

const STEP_CLASS: Record<StepState, string> = {
  done: 'tracker__step--done',
  current: 'tracker__step--current',
  blocked: 'tracker__step--blocked',
  rejected: 'tracker__step--rejected',
  conditional: 'tracker__step--conditional',
  pending: 'tracker__step--pending',
};

export function StageTracker({
  status,
  events,
  submitterName,
}: Readonly<{ status: Status; events: StatusEvent[]; submitterName: string }>) {
  const state = deriveWorkflowState({ status, submitterName });
  const submitted = events.find((e) => e.eventType === 'submitted');
  const itDecision = events.find(
    (e) => e.eventType === 'it_approved' || e.eventType === 'it_rejected',
  );
  const legalDecision = events.find(
    (e) =>
      e.eventType === 'legal_approved' ||
      e.eventType === 'legal_approved_with_conditions' ||
      e.eventType === 'legal_rejected',
  );
  const lastClarification = [...events]
    .reverse()
    .find(
      (e) =>
        e.eventType === 'it_clarification_requested' ||
        e.eventType === 'legal_clarification_requested',
    );

  const itSub =
    state.blockedReason === 'awaiting_submitter_it'
      ? 'Awaiting submitter'
      : itDecision
      ? formatDate(itDecision.createdAt)
      : state.stage === 2
      ? 'In review'
      : 'Pending';
  const legalSub =
    state.blockedReason === 'awaiting_submitter_legal'
      ? 'Awaiting submitter'
      : legalDecision
      ? formatDate(legalDecision.createdAt)
      : state.stage === 3
      ? 'In review'
      : 'Pending';
  const decisionSub = state.ownership === 'closed' ? STATUS_LABELS[status] : 'Pending';

  const steps = [
    { label: 'Submitted', sub: formatDate(submitted?.createdAt) },
    { label: 'IT Review', sub: itSub },
    { label: 'Legal Review', sub: legalSub },
    { label: 'Decision', sub: decisionSub },
  ];

  return (
    <div className="tracker" role="list" aria-label="Workflow stage">
      {steps.map((s, i) => {
        const st = stepStateFor(i, state, status, events);
        const isActive = i + 1 === state.stage;
        return (
          <div
            key={s.label}
            className={`tracker__step ${STEP_CLASS[st]}`}
            role="listitem"
            aria-current={isActive ? 'step' : undefined}
          >
            {i > 0 && <div className="tracker__connector" style={{ left: '-50%', right: '50%' }} />}
            <div className="tracker__node" aria-hidden="true">
              {nodeGlyph(st, i)}
            </div>
            <div className="tracker__label">{s.label}</div>
            <div className="tracker__sub">{s.sub}</div>
          </div>
        );
      })}
      {state.blocked && (
        <div
          className="tracker__blocked-banner"
          style={{
            gridColumn: '1 / -1',
            marginTop: 10,
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(220, 140, 40, 0.10)',
            border: '1px solid rgba(220, 140, 40, 0.35)',
            color: '#7a4a00',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span aria-hidden="true">↶</span>
          <strong>{state.ownerLabel}</strong>
          {lastClarification && (
            <span style={{ opacity: 0.75 }}>
              · clarification requested {formatRelative(lastClarification.createdAt)}
              {lastClarification.comment ? ` — "${truncate(lastClarification.comment, 90)}"` : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + '…';
}

// Visual mapping from category → dot CSS class. The CSS variants
// (timeline__dot--approved, --rejected, --info, --comment) already exist;
// we add --rollback / --responded / --ai / --system as aliases below.
const CATEGORY_TO_DOT: Record<EventCategory, string> = {
  submission: 'submitted',
  reviewer_action: 'approved', // approved is the default — verb disambiguates approved vs rejected
  clarification_requested: 'rollback',
  clarification_responded: 'responded',
  workflow_transition: 'system',
  ai_generation: 'ai',
  audit_meta: 'system',
};

function dotClassFor(e: StatusEvent): string {
  const cat = eventCategory(e.eventType);
  if (e.eventType === 'it_rejected' || e.eventType === 'legal_rejected') return 'rejected';
  if (e.eventType === 'legal_approved_with_conditions') return 'conditional';
  return CATEGORY_TO_DOT[cat];
}

const ACTOR_KIND_LABEL: Record<ReturnType<typeof actorKindFor>, string> = {
  human: 'Human',
  system: 'System',
  ai: 'AI',
};

export function HistoryTimeline({
  events,
  showAllByDefault = false,
  emptyText = 'No activity yet.',
  showRequestLink = false,
  onEventClick,
}: Readonly<{
  events: StatusEvent[];
  showAllByDefault?: boolean;
  emptyText?: string;
  showRequestLink?: boolean;
  onEventClick?: (e: StatusEvent) => void;
}>) {
  // Reverse order client-side — newest at top. DB query stays ASC for the
  // workflow engine's state-replay; rendering owns presentation order.
  const ordered = useMemo(() => [...events].reverse(), [events]);
  const [showAll, setShowAll] = useState(showAllByDefault);

  if (ordered.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 13 }}>
        {emptyText}
      </p>
    );
  }

  const RECENT_COUNT = 5;
  const recent = ordered.slice(0, RECENT_COUNT);
  const older = ordered.slice(RECENT_COUNT);
  const renderedOlder = showAll ? older : [];

  return (
    <ol className="timeline" role="feed" aria-label="Activity timeline, newest first">
      {recent.map((e) => (
        <TimelineRow
          key={e.id}
          ev={e}
          dim={false}
          showRequestLink={showRequestLink}
          onEventClick={onEventClick}
        />
      ))}
      {renderedOlder.map((e) => (
        <TimelineRow
          key={e.id}
          ev={e}
          dim
          showRequestLink={showRequestLink}
          onEventClick={onEventClick}
        />
      ))}
      {older.length > 0 && !showAll && (
        <li className="timeline__item timeline__item--toggle" style={{ listStyle: 'none' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setShowAll(true)}
            style={{ marginLeft: 28 }}
          >
            Show full history ({older.length} older event{older.length === 1 ? '' : 's'})
          </button>
        </li>
      )}
    </ol>
  );
}

function TimelineRow({
  ev,
  dim,
  showRequestLink,
  onEventClick,
}: Readonly<{
  ev: StatusEvent & { requestTicket?: string; requestId?: string };
  dim: boolean;
  showRequestLink: boolean;
  onEventClick?: (e: StatusEvent) => void;
}>) {
  const cat = eventCategory(ev.eventType);
  const actorKind = actorKindFor(cat);
  return (
    <li
      className="timeline__item"
      id={`event-${ev.id}`}
      style={dim ? { opacity: 0.55 } : undefined}
      role="article"
      aria-label={`${ev.actorName} ${eventVerb(ev.eventType)} ${formatRelative(ev.createdAt)}`}
    >
      <span className={`timeline__dot timeline__dot--${dotClassFor(ev)}`} aria-hidden="true" />
      <div className="timeline__head">
        <span className="timeline__actor">{ev.actorName}</span>
        <span className="timeline__action">{eventVerb(ev.eventType)}</span>
        {actorKind !== 'human' && (
          <span
            className="timeline__actor-kind"
            style={{
              marginLeft: 6,
              fontSize: 10,
              padding: '1px 6px',
              borderRadius: 4,
              background: 'rgba(0,0,0,0.06)',
              color: 'var(--color-steel)',
            }}
            title={`Actor type: ${actorKind}`}
          >
            {ACTOR_KIND_LABEL[actorKind]}
          </span>
        )}
        {showRequestLink && ev.requestTicket && onEventClick && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => onEventClick(ev)}
            style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px' }}
          >
            {ev.requestTicket}
          </button>
        )}
        <span className="timeline__time">
          {formatRelative(ev.createdAt)} · {formatDateTime(ev.createdAt)}
        </span>
      </div>
      {ev.comment && <p className="timeline__detail">{ev.comment}</p>}
      {ev.conditions && (
        <p className="timeline__detail">
          <strong>Conditions:</strong> {ev.conditions}
        </p>
      )}
    </li>
  );
}

export function Avatar({ name, size = 28 }: Readonly<{ name: string; size?: number }>) {
  return (
    <span className="comment__avatar" style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {initialsFor(name)}
    </span>
  );
}

// Risk heuristic — same rule the prototype used.
export type Risk = 'low' | 'med' | 'high';
export function riskFor(input: {
  usesConfidentialData: boolean;
  usesPrivilegedData: boolean;
  usesCustomerData: boolean;
}): Risk {
  if (input.usesPrivilegedData && input.usesCustomerData) return 'high';
  if (input.usesConfidentialData || input.usesPrivilegedData || input.usesCustomerData) return 'med';
  return 'low';
}

const RISK_META: Record<Risk, { label: string; cls: string }> = {
  low: { label: 'Low Risk', cls: 'risk-pill--low' },
  med: { label: 'Medium Risk', cls: 'risk-pill--med' },
  high: { label: 'High Risk', cls: 'risk-pill--high' },
};

export function RiskPill({ risk }: Readonly<{ risk: Risk }>) {
  const m = RISK_META[risk];
  return <span className={`risk-pill ${m.cls}`}>{m.label}</span>;
}

const TOOL_PALETTE = [
  { bg: '#DDE5ED', fg: '#002855' },
  { bg: '#E8E3D2', fg: '#7E5400' },
  { bg: '#DCEFE9', fg: '#00563F' },
  { bg: '#DCEBFB', fg: '#08489B' },
  { bg: '#FBE6E6', fg: '#7E2A29' },
  { bg: '#FDF1DE', fg: '#8C4503' },
];

export function ToolLogo({
  name,
  logoUrl,
  size = 40,
}: Readonly<{ name: string; logoUrl?: string | null; size?: number }>) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = !!logoUrl && !imgFailed;

  const letters = name
    .replace(/[^A-Za-z0-9 ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  const p = TOOL_PALETTE[hash % TOOL_PALETTE.length]!;

  if (showImage) {
    return (
      <div
        className="regcard__logo"
        style={{
          width: size,
          height: size,
          background: '#fff',
          color: p.fg,
          padding: 4,
          overflow: 'hidden',
        }}
      >
        <img
          src={logoUrl}
          alt={`${name} logo`}
          referrerPolicy="no-referrer"
          onError={() => setImgFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="regcard__logo"
      style={{ width: size, height: size, background: p.bg, color: p.fg, fontSize: size * 0.34 }}
    >
      {letters || '··'}
    </div>
  );
}

export interface Toast {
  id: string;
  text: string;
  kind?: 'warn' | 'err' | '';
}

export function ToastViewport({
  items,
  onDismiss,
}: Readonly<{ items: Toast[]; onDismiss: (id: string) => void }>) {
  return (
    <div className="toast-wrap">
      {items.map((t) => (
        <div
          key={t.id}
          className={`toast ${t.kind ? `toast--${t.kind}` : ''}`}
          onClick={() => onDismiss(t.id)}
        >
          <span className="toast__dot" />
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}
